/**
 * The workspace's team — every CRM operator account — for assignee pickers,
 * "who sends as what", and the Team page.
 *
 * One fetch shared across the app (module cache, refreshed on demand): the
 * list changes when someone joins the workspace, not between two clicks.
 * The backend syncs new workspace members into the CRM on each read, so a
 * freshly invited teammate appears here without anyone restarting anything.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import type { User } from "./types";

let cache: { at: number; users: User[] } | null = null;
let inFlight: Promise<User[]> | null = null;
const TTL_MS = 60_000;

export async function fetchUsers(force = false): Promise<User[]> {
  if (!force && cache && Date.now() - cache.at < TTL_MS) return cache.users;
  if (inFlight) return inFlight;
  inFlight = api
    .get<{ users: User[] }>("/settings/users")
    .then((d) => {
      cache = { at: Date.now(), users: d.users };
      return d.users;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** Drop the cache (after a Team sync, for example). */
export function invalidateUsers(): void {
  cache = null;
}

export function useUsers() {
  const [users, setUsers] = useState<User[]>(cache?.users ?? []);
  const [loading, setLoading] = useState(!cache);

  const refresh = useCallback(async (force = false) => {
    setLoading(true);
    try {
      setUsers(await fetchUsers(force));
    } catch {
      // Non-fatal everywhere: a picker just shows "Unassigned".
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(false); }, [refresh]);

  return { users, loading, refresh };
}

/** Display name for a user id, or null when unknown / unassigned. */
export function userName(users: User[], id: string | null | undefined): string | null {
  if (!id) return null;
  const u = users.find((x) => x.id === id);
  return u ? (u.name || u.email) : null;
}
